import { getCardBrandDisplay } from "@Light/components/account/PaymentMethod/PaymentMethodCard/PaymentMethodCard";
import { useUpdatePaymentMethodForm } from "@Light/components/page/form/UpdatePaymentMethod";
import { MutationButton } from "@Light/components/page/MutationButton";
import { PageBody } from "@Light/components/page/PageBody";
import { useScaffold } from "@Light/scaffold";
import type { CardPaymentMethodValue } from "@Light/services/lightTypes";
import { CreditCard01 } from "@untitledui/icons";
import clsx from "clsx";
import { useState, type BaseSyntheticEvent } from "react";
import { EnrollHelpButtonFooter } from "../EnrollHelpButtonFooter";
import { useEnrollment } from "../flow/context";
import { useEnrollmentStepper } from "../flow/useEnrollmentStepper";
import { EnrollRoute } from "../routes";
import { ApplePayMark } from "./ApplePayMark";
import { CardBrandMark } from "./CardBrandMark";
import { GooglePayMark } from "./GooglePayMark";
import { NoChargeNotice } from "./NoChargeNotice";
import {
  PaymentOptions,
  PaymentOptionsSkeleton,
  type ExpressPaymentMethod,
  type PaymentMethod,
  type PaymentOption,
} from "./PaymentOptions";
import { StripeExpressCheckoutButtons } from "./StripeExpressCheckoutButtons";

const PAYMENT_METHOD_DISPLAY = {
  card: {
    label: "Credit or debit card",
    icon: <CreditCard01 className="size-5" />,
  },
  apple_pay: { label: "Apple Pay", icon: <ApplePayMark className="w-6" /> },
  google_pay: { label: "Google Pay", icon: <GooglePayMark className="w-6" /> },
} as const;

function makePaymentOptions({
  existingCard,
  expressPaymentMethods,
  cardForm,
}: {
  existingCard: CardPaymentMethodValue | null | undefined;
  expressPaymentMethods: ExpressPaymentMethod[];
  cardForm: React.ReactNode;
}): PaymentOption[] {
  const cardOption: PaymentOption = {
    value: "card",
    ...PAYMENT_METHOD_DISPLAY.card,
    content: (
      <div className="flex flex-col gap-6">
        {cardForm}
        <NoChargeNotice />
      </div>
    ),
  };
  const expressOptions = expressPaymentMethods.map((pm): PaymentOption => ({
    value: pm,
    ...PAYMENT_METHOD_DISPLAY[pm],
    content: <NoChargeNotice />,
  }));

  if (!existingCard) {
    return [cardOption, ...expressOptions];
  }

  return [
    {
      value: "existing",
      label: `${getCardBrandDisplay(existingCard.brand)} ending in ${existingCard.last_four ?? ""}`,
      icon: <CardBrandMark brand={existingCard.brand ?? ""} />,
    },
    { ...cardOption, label: "New credit or debit card" },
    ...expressOptions,
  ];
}

export type PaymentMethodFormProps = {
  existingCard: CardPaymentMethodValue | null | undefined;
  option: PaymentMethod | undefined;
  onOptionChange: (option: PaymentMethod) => void;
  expressPaymentMethods: ExpressPaymentMethod[] | undefined;
  onExpressPaymentMethodsAvailable: (methods: ExpressPaymentMethod[]) => void;
  onExpressCheckoutError: () => void;
};

export const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({
  existingCard,
  option,
  onOptionChange,
  expressPaymentMethods,
  onExpressPaymentMethodsAvailable,
  onExpressCheckoutError,
}) => {
  const scaffold = useScaffold();
  const Field = scaffold.system.field;
  const ErrorMessage = scaffold.system.errorMessage;
  const { next, back, nextMutation } = useEnrollment();
  const stepper = useEnrollmentStepper(EnrollRoute.PaymentSetup);

  const [attempts, setAttempts] = useState(0);

  const { onSubmit, mutation, form, stripeError } = useUpdatePaymentMethodForm(
    false,
    {
      mutate: (paymentMethodId) => {
        void next({ payment_method: { value: paymentMethodId } });
      },
      query: nextMutation,
    },
  );

  const submit = (event?: BaseSyntheticEvent) => {
    event?.preventDefault();
    if (selected === "existing") {
      void next();
      return;
    }
    setAttempts((a) => a + 1);
    void onSubmit();
  };

  const handleExpressCheckout = async (paymentMethodId: string) => {
    try {
      await next(
        { payment_method: { value: paymentMethodId } },
        { throwOnError: true },
      );
    } catch {
      onExpressCheckoutError();
    }
  };

  const isCardOnly = expressPaymentMethods?.length === 0 && !existingCard;
  const selected = isCardOnly ? "card" : option;
  const selectedExpressMethod =
    selected === "card" || selected === "existing" ? undefined : selected;

  const fullStripeErrorMessage =
    !stripeError || attempts < 2
      ? stripeError
      : `${stripeError} You can also contact support at ${scaffold.page.supportEmail} for help.`;

  return (
    <form onSubmit={submit}>
      <PageBody
        onBack={back}
        title="The last thing we need is a payment method"
        subtitle="We use autopay to keep your bills paid on time. You can change your payment method anytime later."
        stepper={stepper}
        footer={
          <EnrollHelpButtonFooter>
            <div className="relative flex flex-col gap-3">
              <ExpressCheckoutCTA
                expressPaymentMethods={expressPaymentMethods}
                selectedExpressPaymentMethod={selectedExpressMethod}
                onPaymentMethodsAvailable={(methods) => {
                  // If we have an express method selected, ignore this
                  // callback since it will only have that method listed.
                  if (selectedExpressMethod) return;
                  onExpressPaymentMethodsAvailable(methods);
                }}
                onPaymentMethod={handleExpressCheckout}
                isSubmitting={Boolean(nextMutation.isLoading)}
              />
              {selectedExpressMethod ? (
                nextMutation.isError && (
                  <Field>
                    <ErrorMessage>
                      Error saving your payment method. Please try again later.
                    </ErrorMessage>
                  </Field>
                )
              ) : (
                <MutationButton
                  size="lg"
                  mutation={selected === "existing" ? nextMutation : mutation}
                  disabled={!selected}
                  mutateButtonText="Save and continue"
                  errorMessage={
                    fullStripeErrorMessage ??
                    "Error submitting payment information. Please try again later."
                  }
                  data-testid="payment-setup-submit"
                />
              )}
            </div>
          </EnrollHelpButtonFooter>
        }
      >
        {expressPaymentMethods ? (
          <PaymentOptions
            value={selected}
            onChange={onOptionChange}
            options={makePaymentOptions({
              existingCard,
              expressPaymentMethods: expressPaymentMethods,
              cardForm: form,
            })}
          />
        ) : (
          <PaymentOptionsSkeleton />
        )}
      </PageBody>
    </form>
  );
};

type ExpressCheckoutCTAProps = {
  expressPaymentMethods: ExpressPaymentMethod[] | undefined;
  selectedExpressPaymentMethod: ExpressPaymentMethod | undefined;
  onPaymentMethodsAvailable: (paymentMethods: ExpressPaymentMethod[]) => void;
  onPaymentMethod: (paymentMethodId: string) => Promise<void>;
  isSubmitting: boolean;
};

const ExpressCheckoutCTA: React.FC<ExpressCheckoutCTAProps> = ({
  expressPaymentMethods,
  selectedExpressPaymentMethod,
  onPaymentMethodsAvailable,
  onPaymentMethod,
  isSubmitting,
}) => {
  const scaffold = useScaffold();
  const Spinner = scaffold.system.spinner;

  if (expressPaymentMethods?.length === 0) {
    return null;
  }

  const only =
    expressPaymentMethods && expressPaymentMethods.length > 1
      ? selectedExpressPaymentMethod
      : undefined;

  return (
    <div
      className={clsx(
        "w-full",
        !selectedExpressPaymentMethod &&
          "pointer-events-none absolute inset-x-0 top-0 opacity-0",
      )}
      aria-hidden={!selectedExpressPaymentMethod}
    >
      <div className="relative">
        <StripeExpressCheckoutButtons
          key={only ?? "all"}
          paymentMethod={only}
          onReady={(ev) => {
            const available = ev.availablePaymentMethods;
            onPaymentMethodsAvailable([
              ...(available?.applePay ? (["apple_pay"] as const) : []),
              ...(available?.googlePay ? (["google_pay"] as const) : []),
            ]);
          }}
          onLoadError={() => {
            onPaymentMethodsAvailable([]);
          }}
          onClick={(ev) => {
            if (isSubmitting) {
              ev.reject();
              return;
            }
            ev.resolve();
          }}
          onPaymentMethod={onPaymentMethod}
        />
        {isSubmitting && (
          <div
            className="absolute inset-0 flex items-center justify-center rounded-md bg-primary/60"
            data-testid="express-checkout-submitting"
          >
            <Spinner className="size-5" />
          </div>
        )}
      </div>
    </div>
  );
};
